import {
  WSList,
  WSLoader,
  WSPage,
  WSPageToolbar
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { ComplianceEntityType } from "@wingspanhq/wingspan-api-sdk";
import { queriesComplianceEntities } from "@query";
import { Tab, Tabs } from "../../../../components/Tabs";
import { useUserId } from "../../../../query/hooks/helpers";
import {
  useQueryNextGenSubscriptions,
  useQuerySubscriptionV3
} from "../../../../query/subscriptions/queries";
import {
  PATH_SETTINGS_ACCOUNT_ACCOUNT,
  PATH_SETTINGS_ACCOUNT_BUSINESS,
  PATH_SETTINGS_ACCOUNT_INFO,
  PATH_SETTINGS_ACCOUNT_MARKETING,
  PATH_SETTINGS_ACCOUNT_MEMBERSHIP,
  PATH_SETTINGS_ACCOUNT_PERSONAL
} from "../../paths";
import { useShowMembershipSettings } from "../../../../userFeatures/useShowMembershipSettings";
import { RouteAccount as RouteAccountTab } from "./routes/RouteAccount";
import { RouteBusinessInformation } from "./routes/RouteBusinessInformation";
import { RouteMarketing } from "./routes/RouteMarketing";
import { RouteMembership } from "./routes/RouteMembership";
import { RoutePersonalInformation } from "./routes/RoutePersonalInformation";
import { ConfirmTaxInformationForLegacyUserAlertWithClients } from "../../../TaxDocuments/components/ConfirmTaxInformationForLegacyUserAlertWithClients";
import { useCurrentTaxFilingYear } from "../../../TaxFiling/utils/useCurrentTaxFilingYear";

export const RouteAccount = () => {
  const userId = useUserId();
  const queryComplianceEntities =
    queriesComplianceEntities.listComplianceEntities.useQuery({});
  const querySubscription = useQuerySubscriptionV3(userId);
  const queryNextGenSubscriptions = useQueryNextGenSubscriptions(userId);
  const currentYear = useCurrentTaxFilingYear();

  const accountType = useMemo(() => {
    const primaryEntity = queryComplianceEntities.data?.data?.find(
      entity => !entity.subject
    );

    return primaryEntity?.type === ComplianceEntityType.BUSINESS
      ? "Business"
      : "Individual";
  }, [queryComplianceEntities.data]);

  const hasSubscription = useMemo(
    () =>
      !!querySubscription.data ||
      (!!queryNextGenSubscriptions.data &&
        queryNextGenSubscriptions.data.length > 0),
    [queryNextGenSubscriptions.data, querySubscription.data]
  );

  const showMembership = useShowMembershipSettings();

  const tabs = useMemo(() => {
    const tabs: Tab[] = [];

    tabs.push({
      label: "Account",
      path: PATH_SETTINGS_ACCOUNT_ACCOUNT
    });

    if (accountType === "Business") {
      tabs.push({
        label: "Business information",
        path: PATH_SETTINGS_ACCOUNT_BUSINESS
      });
    } else {
      tabs.push({
        label: "Personal information",
        path: PATH_SETTINGS_ACCOUNT_PERSONAL
      });
    }

    tabs.push({
      label: "Marketing",
      path: PATH_SETTINGS_ACCOUNT_MARKETING
    });

    if (showMembership && hasSubscription) {
      tabs.push({
        label: "Membership",
        path: PATH_SETTINGS_ACCOUNT_MEMBERSHIP
      });
    }

    return tabs;
  }, [accountType, hasSubscription, showMembership]);

  if (!queryComplianceEntities.isFetched) {
    return (
      <WSPage>
        <WSLoader.Spinner />
      </WSPage>
    );
  }

  return (
    <WSPage>
      <WSPageToolbar title="Account settings" />

      <WSList gap="2XL">
        <ConfirmTaxInformationForLegacyUserAlertWithClients
          currentYear={currentYear}
          showWithNoPayer
        />

        <Tabs tabs={tabs} />

        <Switch>
          <Route
            path={PATH_SETTINGS_ACCOUNT_INFO}
            render={() => {
              if (accountType === "Business") {
                return <Redirect to={PATH_SETTINGS_ACCOUNT_BUSINESS} />;
              } else {
                return <Redirect to={PATH_SETTINGS_ACCOUNT_PERSONAL} />;
              }
            }}
          />

          <Route
            path={PATH_SETTINGS_ACCOUNT_ACCOUNT}
            component={RouteAccountTab}
          />

          <Route
            path={PATH_SETTINGS_ACCOUNT_PERSONAL}
            component={RoutePersonalInformation}
          />
          <Route
            path={PATH_SETTINGS_ACCOUNT_BUSINESS}
            component={RouteBusinessInformation}
          />
          <Route
            path={PATH_SETTINGS_ACCOUNT_MARKETING}
            component={RouteMarketing}
          />
          <Route
            path={PATH_SETTINGS_ACCOUNT_MEMBERSHIP}
            component={RouteMembership}
          />

          <Redirect to={PATH_SETTINGS_ACCOUNT_ACCOUNT} />
        </Switch>
      </WSList>
    </WSPage>
  );
};
